import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  FileText, Building2, Check, Loader2, ArrowRight, AlertTriangle, Shield
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { getDpaStatus, getDpaDocument, acceptDpa, updateEntity, getCurrentEntity } from "../lib/api";
import { toast } from "sonner";

const FIELD_I18N_KEYS = {
  name: "dpa.fieldName",
  cif: "dpa.fieldCif",
  address: "dpa.fieldAddress",
  city: "dpa.fieldCity",
  postal_code: "dpa.fieldPostalCode",
  contact_person: "dpa.fieldContactPerson",
};

function DataCompletionStep({ missingFields, onComplete }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await getCurrentEntity();
        const initial = {};
        missingFields.forEach((f) => { initial[f] = data[f] || ""; });
        setFormData(initial);
      } catch {
        toast.error(t("dpa.errorLoading"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [missingFields, t]);

  const handleSave = async () => {
    const empty = missingFields.filter((f) => !formData[f]?.trim());
    if (empty.length > 0) {
      toast.error(`${t("dpa.fillRequired")}: ${empty.map(f => t(FIELD_I18N_KEYS[f] || f)).join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      await updateEntity(formData);
      toast.success(t("dpa.dataSaved"));
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dpa.errorSave"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-lg p-4">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-amber-800 font-semibold text-sm">{t("dpa.incompleteTitle")}</p>
          <p className="text-amber-700 text-sm mt-1">{t("dpa.incompleteText")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {missingFields.map((field) => (
          <div key={field}>
            <Label className="text-slate-700 text-sm font-medium mb-1.5 block">
              {t(FIELD_I18N_KEYS[field] || field)} <span className="text-red-500">*</span>
            </Label>
            <Input
              value={formData[field] || ""}
              onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-amber-500 focus:ring-amber-500"
              placeholder={t(FIELD_I18N_KEYS[field] || field)}
              data-testid={`dpa-field-${field}`}
            />
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="dpa-save-data-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
          {t("dpa.saveAndContinue")}
        </Button>
      </div>
    </div>
  );
}

function DocumentReviewStep({ onAccepted }) {
  const { t } = useTranslation();
  const [dpaHtml, setDpaHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await getDpaDocument();
        setDpaHtml(data.html);
      } catch {
        toast.error(t("dpa.errorDoc"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [t]);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await acceptDpa();
      toast.success(t("dpa.acceptedToast"));
      onAccepted();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dpa.errorAccept"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-lg p-6 max-h-[500px] overflow-y-auto shadow-inner border border-slate-300"
           data-testid="dpa-document-viewer">
        <div dangerouslySetInnerHTML={{ __html: dpaHtml }} />
      </div>

      <div className="bg-slate-50 border border-slate-300 rounded-lg p-4">
        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="dpa-accept-checkbox-label">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-400 text-emerald-600 focus:ring-emerald-500"
            data-testid="dpa-accept-checkbox"
          />
          <span className="text-sm text-slate-700">
            {t("dpa.acceptCheckbox")}
          </span>
        </label>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleAccept}
          disabled={!accepted || submitting}
          className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
          data-testid="dpa-accept-btn"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Check className="h-4 w-4 mr-2" />
          )}
          {t("dpa.acceptBtn")}
        </Button>
      </div>
    </div>
  );
}

export default function DPAAcceptanceFlow({ onComplete }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("checking");

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const { data } = await getDpaStatus();
      setStatus(data);
      if (data.accepted) {
        setStep("done");
        onComplete();
      } else if (data.missing_fields.length > 0) {
        setStep("data");
      } else {
        setStep("review");
      }
    } catch {
      toast.error(t("dpa.errorStatus"));
      setStep("review");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (step === "done") return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-6 w-6 text-emerald-600" />
          <h2 className="text-xl font-bold text-slate-900">{t("dpa.title")}</h2>
        </div>
        <p className="text-slate-600 text-sm">{t("dpa.subtitle")}</p>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
          step === "data" ? "bg-amber-600 text-white" : "bg-slate-200 text-slate-500"
        }`}>
          <Building2 className="h-3.5 w-3.5" />
          {t("dpa.step1")}
        </div>
        <div className="h-px w-6 bg-slate-300" />
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
          step === "review" ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"
        }`}>
          <FileText className="h-3.5 w-3.5" />
          {t("dpa.step2")}
        </div>
      </div>

      {step === "data" && (
        <DataCompletionStep
          missingFields={status.missing_fields}
          onComplete={() => setStep("review")}
        />
      )}

      {step === "review" && (
        <DocumentReviewStep
          onAccepted={() => {
            setStep("done");
            onComplete();
          }}
        />
      )}
    </motion.div>
  );
}
